/**
 * Year Parsing Utilities
 * Functions for parsing various year formats including BCE dates,
 * Japanese century notations, ranges, and edge cases.
 */

/**
 * Parse years from various string formats
 */
export function parseYears(yearsRaw) {
  if (!yearsRaw || typeof yearsRaw !== "string") {
    return { start: null, end: null, label: "?" };
  }

  let s = yearsRaw.replace(/\s/g, "");

  // Common forms: "1870–1945", "?–1603", "1522–1591", "6世紀?BCE", "1981–"
  // Normalize dash
  s = s.replace(/—|–|−/g, "-");

  // BCE handling
  const bce = /BCE/i.test(s) || /紀.*?BCE/i.test(s);

  // Japanese century hints (very rough): "6世紀" => year ~ -500 to -401 (approx)
  if (/世紀/.test(s)) {
    const m = s.match(/(\d+)/);
    if (m) {
      const century = parseInt(m[1], 10);
      
      if (bce) {
        // Explicitly marked BCE: 6th century BCE ≈ -600 to -501
        const mid = -((century * 100) - 50);
        return { start: mid - 25, end: mid + 25, label: yearsRaw };
      } else if (century < 10) {
        // Early centuries (< 10) treated as BCE by default but different calculation
        const mid = -(century * 100) + 75;
        return { start: mid - 25, end: mid + 25, label: yearsRaw };
      } else {
        // Modern centuries (CE)
        const mid = ((century - 1) * 100) + 50; // e.g., 19C => 1850
        return { start: mid - 25, end: mid + 25, label: yearsRaw };
      }
    }
  }

  // Split by dash if present
  if (s.includes("-")) {
    const [a, b] = s.split("-");
    const toNum = (x) => {
      if (!x || x === "?" || x === "") return null;
      const n = parseInt(x.replace(/[^\d-]/g, ""), 10);
      return isNaN(n) ? null : (bce ? -Math.abs(n) : n);
    };
    const start = toNum(a);
    const end = toNum(b);
    return { start, end, label: yearsRaw };
  }

  // Single year
  const n = parseInt(s.replace(/[^\d-]/g, ""), 10);
  if (!isNaN(n)) {
    const year = bce ? -Math.abs(n) : n;
    return { start: year, end: year, label: yearsRaw };
  }

  return { start: null, end: null, label: yearsRaw };
}

/**
 * Clamp a number between min and max values
 */
export function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

/**
 * Build person to items mapping from reverse index
 */
export function buildPersonToItems(reverseIndex) {
  const map = {};
  if (!reverseIndex) return {};

  for (const [itemLabel, people] of Object.entries(reverseIndex)) {
    (people || []).forEach((p) => {
      const key = (p.person || "").replace(/\s+/g, " ").trim();
      if (!key) return;
      if (!map[key]) map[key] = new Set();
      map[key].add(itemLabel);
    });
  }

  // convert sets to arrays
  const out = {};
  for (const k of Object.keys(map)) {
    out[k] = Array.from(map[k]);
  }
  return out;
}

/**
 * Truncate text to specified length
 */
export function short(text, n = 150) {
  if (!text) return "";
  return text.length > n ? text.slice(0, n) + "…" : text;
}

/**
 * Get era-based color class for a given year
 */
export function eraColor(year) {
  if (year == null) return "bg-gray-400";
  if (year < 1600) return "bg-emerald-600"; // Muromachi/Momoyama and earlier
  if (year < 1868) return "bg-indigo-600";   // Edo
  if (year < 1945) return "bg-rose-600";     // Meiji–Showa early
  return "bg-amber-600";                     // Post-war / modern
}
